import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { authApi, distributionPartnerKeys, distributionPartnersApi } from '@client/api/waterlily';
import type { API } from '@/api/types';

export type DistributionContactWithPartner = API.DistributionPartnerContact & {
  partnerKey: string;
};

interface UseDistributionPartnerConnectionOptions {
  onConnected?: (newlyConnected: DistributionContactWithPartner[]) => void;
}

export function useDistributionPartnerConnection(
  options: UseDistributionPartnerConnectionOptions = {},
) {
  const queryClient = useQueryClient();
  // Multiple firms can be selected; contacts aggregate across all of them.
  const [selectedPartnerKeys, setSelectedPartnerKeys] = useState<string[]>([]);
  const [selectedContactIds, setSelectedContactIds] = useState<string[]>([]);
  // The advisor-level opt-out. Defaults to checked (auto-share on) until the saved record loads.
  const [shareConsent, setShareConsent] = useState(true);

  const partnersQuery = useQuery({
    queryKey: distributionPartnerKeys.partners(),
    queryFn: () => distributionPartnersApi.listPartners(),
    staleTime: 5 * 60 * 1000,
  });

  const sortedPartnerKeys = useMemo(() => [...selectedPartnerKeys].sort(), [selectedPartnerKeys]);

  // Fetch contacts for every selected firm in parallel, tagging each with its partnerKey so the
  // connect step can group them back by firm.
  const contactsQuery = useQuery({
    queryKey: [...distributionPartnerKeys.all, 'contacts-multi', sortedPartnerKeys] as const,
    queryFn: async () => {
      const groups = await Promise.all(
        sortedPartnerKeys.map(async key => {
          const contacts = await distributionPartnersApi.getContacts(key);
          return contacts.map(contact => ({ ...contact, partnerKey: key }));
        }),
      );

      return groups.flat() as DistributionContactWithPartner[];
    },
    enabled: sortedPartnerKeys.length > 0,
    staleTime: 60 * 1000,
  });

  const consentQuery = useQuery({
    queryKey: distributionPartnerKeys.sharingConsent(),
    queryFn: () => distributionPartnersApi.getSharingConsent(),
    staleTime: 60 * 1000,
  });

  const legacyClientsQuery = useQuery({
    queryKey: distributionPartnerKeys.hasLegacyClients(),
    queryFn: () => distributionPartnersApi.getHasLegacyClients(),
    staleTime: 60 * 1000,
  });

  // Reflect the saved opt-out once it loads. A null record (never set) keeps the default-on state.
  useEffect(() => {
    const saved = consentQuery.data?.hasConsented;

    if (typeof saved === 'boolean') {
      setShareConsent(saved);
    }
  }, [consentQuery.data]);

  // Restore the partners the advisor is already connected to once the list loads, so a page reload
  // shows their saved selection. Runs once; the advisor controls the selection afterwards.
  const didInitConnectedPartnersRef = useRef(false);
  useEffect(() => {
    if (didInitConnectedPartnersRef.current || !partnersQuery.data) return;

    const connectedKeys = partnersQuery.data
      .filter(partner => partner.isConnected)
      .map(partner => partner.partnerKey);

    didInitConnectedPartnersRef.current = true;

    if (connectedKeys.length > 0) {
      setSelectedPartnerKeys(prev => Array.from(new Set([...prev, ...connectedKeys])));
    }
  }, [partnersQuery.data]);

  // Pre-check contacts the advisor is already connected with, so the list reflects who they've
  // already connected. Merged into the selection (never removes the advisor's own picks).
  useEffect(() => {
    if (!contactsQuery.data) return;

    const connectedIds = contactsQuery.data
      .filter(contact => contact.isConnected)
      .map(contact => contact.advisorId);

    if (connectedIds.length === 0) return;

    setSelectedContactIds(prev => Array.from(new Set([...prev, ...connectedIds])));
  }, [contactsQuery.data]);

  const contacts = contactsQuery.data ?? [];

  const partnerKeyByContactId = useMemo(() => {
    const map = new Map<string, string>();
    contacts.forEach(contact => map.set(contact.advisorId, contact.partnerKey));
    return map;
  }, [contacts]);

  const togglePartner = useCallback(
    (partnerKey: string) => {
      const isDeselecting = selectedPartnerKeys.includes(partnerKey);

      setSelectedPartnerKeys(prev =>
        isDeselecting ? prev.filter(key => key !== partnerKey) : [...prev, partnerKey],
      );

      // Deselecting a partner drops its contacts from the selection, so "Select all" and the chosen
      // contacts reset instead of lingering when the partner is re-selected.
      if (isDeselecting) {
        setSelectedContactIds(ids =>
          ids.filter(id => partnerKeyByContactId.get(id) !== partnerKey),
        );
      }
    },
    [selectedPartnerKeys, partnerKeyByContactId],
  );

  const toggleContact = useCallback((advisorId: string, checked: boolean) => {
    setSelectedContactIds(prev => {
      const set = new Set(prev);

      if (checked) {
        set.add(advisorId);
      } else {
        set.delete(advisorId);
      }

      return Array.from(set);
    });
  }, []);

  const toggleContacts = useCallback((advisorIds: string[], checked: boolean) => {
    setSelectedContactIds(prev => {
      const set = new Set(prev);

      advisorIds.forEach(id => (checked ? set.add(id) : set.delete(id)));

      return Array.from(set);
    });
  }, []);

  const connectMutation = useMutation({
    // Connect each firm's newly selected contacts. autoShare decides whether to also share the
    // advisor's clients with them now (per-contact); already-connected contacts are managed via the
    // per-contact sharing toggle, so they're excluded here.
    mutationFn: async (autoShare: boolean) => {
      const connectedIds = new Set(
        contacts.filter(contact => contact.isConnected).map(contact => contact.advisorId),
      );

      const byPartner = new Map<string, string[]>();
      const newlyConnectedIds: string[] = [];

      selectedContactIds.forEach(id => {
        if (connectedIds.has(id)) return;

        const key = partnerKeyByContactId.get(id);

        if (!key) return;

        byPartner.set(key, [...(byPartner.get(key) ?? []), id]);
        newlyConnectedIds.push(id);
      });

      for (const [partnerKey, contactIds] of byPartner) {
        await distributionPartnersApi.connect(partnerKey, contactIds, autoShare);
      }

      return { newlyConnectedIds, autoShare };
    },
    onSuccess: ({ newlyConnectedIds, autoShare }) => {
      toast.success('Partner contacts connected');
      // Reflect connected (and, if chosen, sharing) state on the cards immediately, before the
      // refetch — otherwise the badges wouldn't update until the contacts query revalidates.
      const newlyConnected = new Set(newlyConnectedIds);
      const newlyConnectedContacts = contacts.filter(contact => newlyConnected.has(contact.advisorId));
      queryClient.setQueriesData<DistributionContactWithPartner[]>(
        { queryKey: [...distributionPartnerKeys.all, 'contacts-multi'] },
        prev =>
          prev?.map(contact =>
            newlyConnected.has(contact.advisorId)
              ? { ...contact, isConnected: true, isSharing: autoShare }
              : contact,
          ),
      );
      // Newly connected contacts have no past clients shared yet (future-sharing never shares past
      // clients), so the global "Share past clients" banner becomes relevant again. Patch its cache
      // directly so it reappears immediately instead of waiting on the refetch.
      if (newlyConnectedIds.length > 0) {
        queryClient.setQueryData<API.DistributionPartnerHasLegacyClients>(
          distributionPartnerKeys.hasLegacyClients(),
          prev =>
            prev
              ? {
                  ...prev,
                  hasConnections: true,
                  hasUnsharedLegacyClients: prev.hasLegacyClients
                    ? true
                    : prev.hasUnsharedLegacyClients,
                }
              : prev,
        );
      }
      queryClient.invalidateQueries({ queryKey: distributionPartnerKeys.all });
      options.onConnected?.(newlyConnectedContacts);
    },
    onError: (error: unknown) => {
      toast.error('Failed to connect contacts', {
        description: error instanceof Error ? error.message : 'Please try again.',
      });
    },
  });

  const disconnectMutation = useMutation({
    mutationFn: ({
      partnerKey,
      contactAdvisorId,
      scope,
    }: {
      partnerKey: string;
      contactAdvisorId: string;
      scope: API.DistributionPartnerDisconnectScope;
    }) => distributionPartnersApi.disconnect(partnerKey, contactAdvisorId, scope),
    onSuccess: (_data, { contactAdvisorId, scope }) => {
      // Drop the contact from the in-session selection and optimistically clear its isConnected
      // flag, so the card unchecks and the trash button disappears immediately — before the
      // contacts query refetches.
      setSelectedContactIds(prev => prev.filter(id => id !== contactAdvisorId));
      queryClient.setQueriesData<DistributionContactWithPartner[]>(
        { queryKey: [...distributionPartnerKeys.all, 'contacts-multi'] },
        prev =>
          prev?.map(contact =>
            contact.advisorId === contactAdvisorId ? { ...contact, isConnected: false } : contact,
          ),
      );
      queryClient.invalidateQueries({ queryKey: distributionPartnerKeys.all });

      toast.success(
        scope === 'all'
          ? 'Disconnected and stopped sharing all clients'
          : 'Stopped sharing future clients',
      );
    },
    onError: (error: unknown) => {
      toast.error('Failed to update sharing', {
        description: error instanceof Error ? error.message : 'Please try again.',
      });
    },
  });

  const setContactSharingMutation = useMutation({
    mutationFn: ({
      partnerKey,
      contactAdvisorId,
      autoShare,
    }: {
      partnerKey: string;
      contactAdvisorId: string;
      autoShare: boolean;
    }) => distributionPartnersApi.setContactSharing(partnerKey, contactAdvisorId, autoShare),
    onSuccess: (_data, { contactAdvisorId, autoShare }) => {
      // Reflect the new sharing state on the contact card immediately, before the refetch.
      queryClient.setQueriesData<DistributionContactWithPartner[]>(
        { queryKey: [...distributionPartnerKeys.all, 'contacts-multi'] },
        prev =>
          prev?.map(contact =>
            contact.advisorId === contactAdvisorId ? { ...contact, isSharing: autoShare } : contact,
          ),
      );
      queryClient.invalidateQueries({ queryKey: distributionPartnerKeys.all });

      toast.success(
        autoShare
          ? 'Now sharing your clients with this contact'
          : 'Stopped sharing your clients with this contact',
      );
    },
    onError: (error: unknown) => {
      toast.error('Failed to update sharing', {
        description: error instanceof Error ? error.message : 'Please try again.',
      });
    },
  });

  const shareContactLegacyMutation = useMutation({
    mutationFn: ({
      partnerKey,
      contactAdvisorId,
      share,
    }: {
      partnerKey: string;
      contactAdvisorId: string;
      share: boolean;
    }) => distributionPartnersApi.shareContactLegacyClients(partnerKey, contactAdvisorId, share),
    onSuccess: (_data, { contactAdvisorId, share }) => {
      queryClient.setQueriesData<DistributionContactWithPartner[]>(
        { queryKey: [...distributionPartnerKeys.all, 'contacts-multi'] },
        prev =>
          prev?.map(contact =>
            contact.advisorId === contactAdvisorId ? { ...contact, legacyShared: share } : contact,
          ),
      );
      // Stopping leaves at least one connected contact without past clients, so the global "Share
      // past clients" banner becomes relevant again — patch its cache so it reappears immediately.
      if (!share) {
        queryClient.setQueryData<API.DistributionPartnerHasLegacyClients>(
          distributionPartnerKeys.hasLegacyClients(),
          prev => (prev ? { ...prev, hasUnsharedLegacyClients: true } : prev),
        );
      }
      queryClient.invalidateQueries({ queryKey: distributionPartnerKeys.all });

      toast.success(
        share
          ? 'Your earlier clients were shared with this contact'
          : 'Stopped sharing your earlier clients with this contact',
      );
    },
    onError: (error: unknown) => {
      toast.error('Failed to update earlier-client sharing', {
        description: error instanceof Error ? error.message : 'Please try again.',
      });
    },
  });

  // Batch per-contact legacy share: share earlier clients with a specific SET of contacts (the ones
  // just connected), scoping the share to only those contacts instead of everyone. Shares run
  // sequentially so the one-time attestation isn't double-recorded by concurrent requests, then the
  // cache is patched and a single summary toast is shown.
  const shareContactsLegacyMutation = useMutation({
    mutationFn: async (targets: { partnerKey: string; contactAdvisorId: string }[]) => {
      for (const { partnerKey, contactAdvisorId } of targets) {
        await distributionPartnersApi.shareContactLegacyClients(partnerKey, contactAdvisorId, true);
      }

      return targets.map(target => target.contactAdvisorId);
    },
    onSuccess: sharedContactIds => {
      const sharedSet = new Set(sharedContactIds);
      queryClient.setQueriesData<DistributionContactWithPartner[]>(
        { queryKey: [...distributionPartnerKeys.all, 'contacts-multi'] },
        prev =>
          prev?.map(contact =>
            sharedSet.has(contact.advisorId) ? { ...contact, legacyShared: true } : contact,
          ),
      );
      queryClient.invalidateQueries({ queryKey: distributionPartnerKeys.all });

      toast.success(
        sharedContactIds.length === 1
          ? 'Your earlier clients were shared with this contact'
          : `Your earlier clients were shared with ${sharedContactIds.length} contacts`,
      );
    },
    onError: (error: unknown) => {
      toast.error('Failed to share your earlier clients', {
        description: error instanceof Error ? error.message : 'Please try again.',
      });
    },
  });

  const requestOtherMutation = useMutation({
    mutationFn: ({
      name,
      email,
      organizationName,
    }: {
      name: string;
      email: string;
      organizationName?: string;
    }) =>
      distributionPartnersApi.requestContact(
        name.trim(),
        email.trim(),
        organizationName?.trim() || undefined,
      ),
    onSuccess: () => {
      toast.success('Invite sent', {
        description: "We've emailed them an invitation to join Waterlily as your partner.",
      });
      // A brand-new domain may have just created a pending partner — refresh so it appears (badged)
      // in the advisor's partner list.
      queryClient.invalidateQueries({ queryKey: distributionPartnerKeys.partners() });
    },
    onError: (error: unknown) => {
      toast.error('Failed to send request', {
        description: error instanceof Error ? error.message : 'Please try again.',
      });
    },
  });

  // Batch legacy-client share (Modal 2 / settings): record the attestation and share all of the
  // advisor's pre-new-consent clients with their connected partner contacts.
  const legacyShareMutation = useMutation({
    mutationFn: (hasConsented: boolean) => distributionPartnersApi.shareLegacyClients(hasConsented),
    onSuccess: (_data, hasConsented) => {
      if (hasConsented) {
        // Earlier clients are now shared with every connected contact — flip their badges
        // immediately, then revalidate the whole tree (contacts + the "share with everyone" banner).
        queryClient.setQueriesData<DistributionContactWithPartner[]>(
          { queryKey: [...distributionPartnerKeys.all, 'contacts-multi'] },
          prev =>
            prev?.map(contact =>
              contact.isConnected ? { ...contact, legacyShared: true } : contact,
            ),
        );
        // Every connected contact now has the past clients, so the global banner no longer applies.
        queryClient.setQueryData<API.DistributionPartnerHasLegacyClients>(
          distributionPartnerKeys.hasLegacyClients(),
          prev => (prev ? { ...prev, hasUnsharedLegacyClients: false } : prev),
        );
        toast.success('Your earlier clients were shared with your partner contacts');
      }

      queryClient.invalidateQueries({ queryKey: distributionPartnerKeys.all });
    },
    onError: (error: unknown) => {
      toast.error('Failed to share your earlier clients', {
        description: error instanceof Error ? error.message : 'Please try again.',
      });
    },
  });

  const connect = useCallback(
    (autoShare: boolean) => {
      if (selectedContactIds.length === 0) return;

      connectMutation.mutate(autoShare);
    },
    [selectedContactIds, connectMutation],
  );

  const requestOther = useCallback(
    (name: string, email: string, organizationName?: string, onDone?: () => void) => {
      if (!name.trim() || !email.trim()) return;

      requestOtherMutation.mutate(
        { name, email, organizationName },
        { onSuccess: () => onDone?.() },
      );
    },
    [requestOtherMutation],
  );

  // The invite path is only for people without a Waterlily account. Check before inviting so an
  // existing advisor is blocked up front (no misleading "they need to create an account" prompt,
  // and no unintended invite email) rather than only being caught by the server's guard.
  const checkContactExistsMutation = useMutation({
    mutationFn: (email: string) => authApi.postAdvisorExistsForPublic({ email }),
  });

  const checkContactHasAccount = useCallback(
    async (email: string): Promise<boolean> => {
      const trimmedEmail = email.trim();
      if (!trimmedEmail) return false;

      try {
        const { exists } = await checkContactExistsMutation.mutateAsync(trimmedEmail);

        if (exists) {
          toast.error('This person already has a Waterlily account', {
            description:
              'They can be connected directly instead of invited — search for them in the list above, or use a different email.',
          });
        }

        return exists;
      } catch {
        // If the lookup itself fails, don't block the invite — the server still rejects existing
        // accounts, so the advisor gets an error either way.
        return false;
      }
    },
    [checkContactExistsMutation],
  );

  const disconnect = useCallback(
    (
      partnerKey: string,
      contactAdvisorId: string,
      scope: API.DistributionPartnerDisconnectScope,
      onDone?: () => void,
    ) => {
      if (!partnerKey || !contactAdvisorId) return;

      disconnectMutation.mutate(
        { partnerKey, contactAdvisorId, scope },
        { onSuccess: () => onDone?.() },
      );
    },
    [disconnectMutation],
  );

  const setContactSharing = useCallback(
    (partnerKey: string, contactAdvisorId: string, autoShare: boolean, onDone?: () => void) => {
      if (!partnerKey || !contactAdvisorId) return;

      setContactSharingMutation.mutate(
        { partnerKey, contactAdvisorId, autoShare },
        { onSuccess: () => onDone?.() },
      );
    },
    [setContactSharingMutation],
  );

  const confirmLegacyShare = useCallback(
    (onDone?: () => void) => {
      legacyShareMutation.mutate(true, { onSuccess: () => onDone?.() });
    },
    [legacyShareMutation],
  );

  const shareContactLegacy = useCallback(
    (partnerKey: string, contactAdvisorId: string, share: boolean, onDone?: () => void) => {
      if (!partnerKey || !contactAdvisorId) return;

      shareContactLegacyMutation.mutate(
        { partnerKey, contactAdvisorId, share },
        { onSuccess: () => onDone?.() },
      );
    },
    [shareContactLegacyMutation],
  );

  const shareContactsLegacy = useCallback(
    (
      targets: { partnerKey: string; contactAdvisorId: string }[],
      onDone?: () => void,
    ) => {
      if (targets.length === 0) return;

      shareContactsLegacyMutation.mutate(targets, { onSuccess: () => onDone?.() });
    },
    [shareContactsLegacyMutation],
  );

  return {
    partners: partnersQuery.data ?? [],
    isLoadingPartners: partnersQuery.isLoading,
    contacts,
    isLoadingContacts: contactsQuery.isLoading,
    selectedPartnerKeys,
    selectedContactIds,
    togglePartner,
    toggleContact,
    toggleContacts,
    connect,
    isConnecting: connectMutation.isPending,
    requestOther,
    isRequestingOther: requestOtherMutation.isPending,
    checkContactHasAccount,
    isCheckingContact: checkContactExistsMutation.isPending,
    disconnect,
    isDisconnecting: disconnectMutation.isPending,
    setContactSharing,
    isSettingSharing: setContactSharingMutation.isPending,
    // The saved opt-out, used to pre-select the checkbox in the connect confirmation dialog.
    shareConsent,
    hasLegacyClients: legacyClientsQuery.data?.hasLegacyClients ?? false,
    hasConnections: legacyClientsQuery.data?.hasConnections ?? false,
    hasLegacyShareConsent: legacyClientsQuery.data?.hasLegacyShareConsent ?? false,
    hasUnsharedLegacyClients: legacyClientsQuery.data?.hasUnsharedLegacyClients ?? false,
    confirmLegacyShare,
    isSharingLegacy: legacyShareMutation.isPending,
    shareContactLegacy,
    isSharingContactLegacy: shareContactLegacyMutation.isPending,
    shareContactsLegacy,
    isSharingContactsLegacy: shareContactsLegacyMutation.isPending,
  };
}
